import { useCallback, useEffect, useRef } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { useNotification } from '@/js/libs/ui/src/V2';

type UseSuccessNotification = (message: string, title?: string) => () => void;

/**
 * To be used with useRequest. Returns a memoized function that shows a success notification.
 * @param message - Notification message to be shown (pass already translated)
 * @param title - Optional notification title (pass already translated). If not provided
 * "Success" will be used.
 * If not provided - "Success" will be used as notification title.
 * @returns A function to show success notification for `useRequest`.
 */
export const useSuccessNotification: UseSuccessNotification = (message, title) => {
  const { success } = useNotification();
  const { t } = useTranslation(['backend', 'common']);

  // NOTE: We do not want the callback to be different, but still want to use the actual `t` retrieved via hook
  const tFunctionRef = useRef<TFunction>(t);

  useEffect(() => {
    tFunctionRef.current = t;
  }, [t, tFunctionRef]);

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-restricted-syntax
  return useCallback(
    () =>
      success({
        title: title || t('common:SUCCESS'),
        body: message || '',
      }),
    [success, title, t, message],
  );
};
